import { useSyncExternalStore } from 'react';
import Tooltip from '@mui/material/Tooltip';
import styled from 'styled-components';
import { useSlateStatic } from '../../../core';
import type { RenderLeafProps, LeafPosition } from '../../../core';
import { DICTATION_CHECK_AI_DISCLAIMER } from '../../../../ReportCorrections/constants';
import { accept, dismiss, revert } from '../actions';
import { getDictationCheckStore } from '../store';
import Colors from 'styles/colors';
import { Button } from 'common/ui/Button';

interface DictationCheckLeafProps extends RenderLeafProps {
  leafPosition?: LeafPosition;
}

export const DictationCheckLeaf = ({
  leaf,
  children,
  leafPosition,
}: DictationCheckLeafProps): React.ReactElement => {
  const editor = useSlateStatic();
  const store = getDictationCheckStore(editor);
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const mark = leaf.dictationCheck;
  // Open state lives in the store, not here. Focusing a mark makes the editor decorate again,
  // and Slate can recreate a leaf, which would reset local state and close the popover at once.
  const anchor = String(leafPosition?.start ?? 0);
  const item = snapshot.items.find((candidate) => candidate.change.id === mark?.id);
  const isFaded = !!snapshot.focusedId && snapshot.focusedId !== mark?.id;
  const replacement =
    !leafPosition || leafPosition.end === mark?.endOffset ? mark?.replacement : undefined;
  return (
    <Tooltip
      placement="bottom-start"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: Colors.gray3,
            color: Colors.gray10,
            border: `1px solid ${Colors.gray8}`,
            borderRadius: '6px',
            fontSize: '12px',
            lineHeight: 1.5,
            padding: '12px',
            maxWidth: 'min(344px, calc(100vw - 16px))',
            maxHeight: 'calc(100vh - 16px)',
            overflowY: 'auto',
            '&.MuiTooltip-tooltipPlacementBottom': { marginTop: 0 },
            '&.MuiTooltip-tooltipPlacementTop': { marginBottom: 0 },
            boxSizing: 'border-box',
            overflowWrap: 'anywhere',
          },
        },
        popper: {
          modifiers: [
            { name: 'offset', options: { offset: [0, 4] } },
            { name: 'preventOverflow', options: { padding: 8 } },
            { name: 'flip', options: { padding: 8 } },
          ],
        },
      }}
      enterDelay={0}
      leaveDelay={100}
      open={!!item && snapshot.focusedId === mark?.id && snapshot.focusedAnchor === anchor}
      onOpen={() => store.focus(mark?.id, anchor)}
      onClose={() => {
        const current = store.getSnapshot();
        if (current.focusedId === mark?.id && current.focusedAnchor === anchor) store.focus();
      }}
      title={
        item ? (
          <Popover
            contentEditable={false}
            data-dd-privacy="mask"
            role="dialog"
            aria-label="Dictation suggestion"
          >
            <p>{item.change.reason}</p>
            <Disclaimer>{DICTATION_CHECK_AI_DISCLAIMER}</Disclaimer>
            {item.status === 'applied' ? (
              <Button
                variant="secondary"
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => revert(editor, item.change.id)}
              >
                Revert to {item.change.original || '(empty)'}
              </Button>
            ) : (
              <>
                <Button
                  variant="primary"
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => accept(editor, item.change.id)}
                >
                  Accept
                </Button>{' '}
                <Button
                  variant="ghost"
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => dismiss(editor, item.change.id)}
                >
                  Dismiss
                </Button>
              </>
            )}
          </Popover>
        ) : (
          ''
        )
      }
    >
      <Suggestion
        $isFaded={isFaded}
        $isApplied={item?.status === 'applied'}
        data-dictation-check-faded={isFaded || undefined}
        data-dd-privacy="mask"
        data-dictation-check-id={leaf.dictationCheck?.id}
        data-replacement={replacement ? ` → ${replacement}` : undefined}
        aria-label={
          item?.status === 'applied'
            ? `Applied correction: ${item.change.replacement || 'delete'}`
            : replacement !== undefined
              ? `Suggested replacement: ${replacement || 'delete'}`
              : undefined
        }
      >
        {children}
      </Suggestion>
    </Tooltip>
  );
};

const Suggestion = styled.span<{ $isFaded: boolean; $isApplied: boolean }>`
  color: ${Colors.red6};
  /* Keep contrast on disabled and focus-mode editor backgrounds as well. */
  background-color: ${Colors.gray2};
  background-image: linear-gradient(${Colors.orange4}26, ${Colors.orange4}26);
  text-decoration: underline;
  text-decoration-color: ${Colors.orange4};
  ${({ $isApplied }) =>
    $isApplied &&
    `
    color: ${Colors.green4};
    font-weight: bold;
    background-image: linear-gradient(${Colors.green4}26, ${Colors.green4}26);
    text-decoration-color: ${Colors.green4};
  `}
  ${({ $isFaded }) =>
    $isFaded &&
    `font-weight: inherit;
     color: inherit;
     background: none;
     text-decoration: none;`}
  &::after {
    content: ${({ $isFaded }) => ($isFaded ? 'none' : 'attr(data-replacement)')};
    color: ${Colors.green4};
    background-color: ${Colors.gray1};
    text-decoration: none;
  }
`;

const Popover = styled.div`
  max-width: 320px;
  font-weight: normal;
  white-space: normal;
  p {
    margin: 0 0 10px;
  }
`;

const Disclaimer = styled.p`
  color: ${Colors.gray8};
`;
